import { sleep } from "./sleep"

type PromiseWithRetryOptions<T> = {
  onRetry?: (error: Error, attempt: number) => void;
  onError?: (error: Error) => void;
  onSuccess?: (data: T) => void;
  onFinally?: () => void;
  retryDelay?: number; // Delay in ms
};

/**
 * Attempts to execute an asynchronous function multiple times with delays between retries.
 *
 * @param asyncFn The asynchronous function to execute.
 * @param maxRetries The maximum number of retry attempts.
 * @param options Optional callbacks and configuration.
 * @returns A promise that resolves with the result of the async function if successful, or rejects if all retries fail.
 */
export async function promiseWithRetry<T>(
  asyncFn: () => Promise<T>,
  maxRetries: number,
  options: PromiseWithRetryOptions<T> = {}
): Promise<T | undefined> {
  const {
    onRetry = () => {},
    onError = () => {},
    onSuccess = () => {},
    onFinally = () => {},
    retryDelay = 1000, // Default 1 second delay
  } = options;

  let attempt = 0;
  while (attempt <= maxRetries) {
    try {
      const data = await asyncFn();
      onSuccess(data);
      onFinally();
      return data;
    } catch (error: any) {
      attempt++;
      console.error(`Attempt ${attempt} failed:`, error);
      onRetry(error, attempt);

      if (attempt > maxRetries) {
        console.error('Max retries reached. Failing.');
        onError(error);
        onFinally();
        // We don't re-throw here to let the caller decide how to handle the final error via onError
        return undefined; // Indicate failure after retries
      }

      // Wait before the next retry
      if (retryDelay > 0) {
        await sleep(retryDelay);
      }
    }
  }
  // Should theoretically not be reached if maxRetries >= 0, but satisfies TS
  onFinally(); 
  return undefined; 
} 